import React, { useState, useEffect, useMemo } from 'react';
import {
    Truck,
    Search,
    ShieldCheck,
    ShieldX,
    CheckCircle,
    AlertCircle,
    Settings,
    Trash2,
    ExternalLink,
    X,
    Loader2,
    Star,
    MapPin
} from 'lucide-react';
import CityCombobox from '../components/CityCombobox';
import { ensureCitiesLoaded } from '../services/citiesService';
import { useTranslation } from 'react-i18next';
import {
    getDeliveryCompanies,
    connectDeliveryCompany,
    disconnectDeliveryCompany,
    makeDefaultDeliveryCompany,
    testDeliveryConnection
} from '../services/api';

const DeliveryCompanies = () => {
    const { t } = useTranslation();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'connected', 'disconnected'
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [credentials, setCredentials] = useState({});
    const [settings, setSettings] = useState({});
    const [pickupCities, setPickupCities] = useState([]);
    const [loadingPickupCities, setLoadingPickupCities] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (modalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [modalOpen]);

    const fetchCompanies = async () => {
        try {
            const res = await getDeliveryCompanies();
            setCompanies(res.data);
            setLoading(false);
        } catch (error) {
            showToast('error', t('delivery_companies.messages.fetch_error'));
            setLoading(false);
        }
    };

    const showToast = (type, message) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const handleConnect = async (company) => {
        setSelectedCompany(company);
        // Pre-fill fields based on company.credentialFields from backend
        const fields = company.credentialFields || ['apiKey'];
        const initialCredentials = {};
        fields.forEach(field => {
            initialCredentials[field] = '';
        });
        setCredentials(initialCredentials);
        setSettings(company.settings || {});
        
        if (company.slug === 'sendit') {
            setLoadingPickupCities(true);
            try {
                // We need credentials to fetch cities for Sendit, so we can only do this if already connected
                // or after user enters them. For now, let's just pre-fetch if possible.
                // Actually, ensureCitiesLoaded will handle it if we have a connection.
                const cities = await ensureCitiesLoaded('sendit');
                setPickupCities(cities);
            } catch (err) {
                console.error("Error fetching pickup cities:", err);
            } finally {
                setLoadingPickupCities(false);
            }
        }
        
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedCompany(null);
        setCredentials({});
        setSettings({});
    };

    const submitConnection = async () => {
        // Validation: ensure all fields have values
        const missingFields = Object.entries(credentials).filter(([_, v]) => !v || v.trim() === '');
        if (missingFields.length > 0) {
            showToast('error', t('delivery_companies.messages.fill_fields'));
            return;
        }

        try {
            const res = await connectDeliveryCompany(selectedCompany._id, credentials, settings);
            showToast('success', t('delivery_companies.messages.connect_success', { name: selectedCompany.name }));
            closeModal();
            fetchCompanies();
        } catch (error) {
            showToast('error', error.response?.data?.message || t('delivery_companies.messages.connect_error'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleFetchCitiesForSendit = async () => {
        if (!credentials.publicKey || (!credentials.privateKey && !credentials.secretKey)) {
            showToast('error', "Veuillez entrer les clés API d'abord");
            return;
        }
        setLoadingPickupCities(true);
        try {
            const fetched = await ensureCitiesLoaded('sendit', credentials);
            setPickupCities(fetched);
            if (fetched.length === 0) {
                showToast('error', "Aucune ville trouvée (vérifiez vos clés)");
            } else {
                showToast('success', `${fetched.length} villes chargées!`);
            }
        } catch (err) {
            console.error(err);
            showToast('error', "Erreur lors du chargement des villes");
        } finally {
            setLoadingPickupCities(false);
        }
    };

    const handleDisconnect = async (company) => {
        if (!window.confirm(t('delivery_companies.messages.disconnect_confirm', { name: company.name }))) {
            return;
        }
        try {
            await disconnectDeliveryCompany(company._id);
            showToast('success', t('delivery_companies.messages.disconnect_success', { name: company.name }));
            fetchCompanies();
        } catch (error) {
            showToast('error', t('delivery_companies.messages.disconnect_error'));
        }
    };

    const handleMakeDefault = async (company) => {
        try {
            await makeDefaultDeliveryCompany(company._id);
            showToast('success', t('delivery_companies.messages.default_success', { name: company.name }));
            fetchCompanies();
        } catch (error) {
            showToast('error', t('delivery_companies.messages.default_error'));
        }
    };

    const handleTestConnection = async (company) => {
        try {
            const res = await testDeliveryConnection(company._id);
            showToast('success', res.message);
        } catch (error) {
            showToast('error', t('delivery_companies.messages.test_error'));
        }
    };

    const filteredCompanies = useMemo(() => {
        return companies.filter(c => {
            const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase());
            const matchesFilter = filter === 'all' ||
                (filter === 'connected' && c.isConnected) ||
                (filter === 'disconnected' && !c.isConnected);
            return matchesSearch && matchesFilter;
        });
    }, [companies, search, filter]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="px-4 py-6 md:p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-gray-900 mb-1 flex items-center gap-2">
                        <Truck className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                        {t('delivery_companies.title')}
                    </h1>
                    <p className="text-sm text-gray-500 font-medium">{t('delivery_companies.subtitle')}</p>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full md:w-auto">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('delivery_companies.search_placeholder')}
                            className="pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none w-full md:w-64 transition-all text-xs md:text-sm font-bold shadow-sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-8 bg-gray-100/50 p-1.5 rounded-2xl w-fit border border-gray-100">
                {['all', 'connected', 'disconnected'].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold tracking-tight transition-all ${filter === f
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                            }`}
                    >
                        {t(`delivery_companies.filters.${f}`)}
                    </button>
                ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCompanies.map((company) => (
                    <div key={company._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group">
                        <div className="p-5 md:p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 group-hover:bg-blue-50 transition-colors">
                                    <Truck className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                </div>
                                <div className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${company.isConnected
                                    ? 'bg-green-50 text-green-600'
                                    : 'bg-gray-50 text-gray-500'
                                    }`}>
                                    {company.isConnected ? (
                                        <><ShieldCheck className="w-3.5 h-3.5" /> {t('delivery_companies.card.connected')}</>
                                    ) : (
                                        <><ShieldX className="w-3.5 h-3.5" /> {t('delivery_companies.card.disconnected')}</>
                                    )}
                                </div>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-lg font-bold text-gray-900">{company.name}</h3>
                                </div>
                                <p className="text-sm text-gray-500">
                                    {company.isConnected
                                        ? t('delivery_companies.card.integrated_with', { fields: company.credentialFields?.join(', ') })
                                        : t('delivery_companies.card.connect_cta')}
                                </p>
                            </div>

                            <div className="flex flex-col gap-2">
                                {company.isConnected ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={() => handleTestConnection(company)}
                                            className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold transition-colors"
                                        >
                                            <ExternalLink className="w-4 h-4" /> {t('delivery_companies.card.test')}
                                        </button>
                                        <button
                                            onClick={() => handleDisconnect(company)}
                                            className="flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-semibold transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" /> {t('delivery_companies.card.remove')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => handleConnect(company)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm shadow-blue-200"
                                    >
                                        <Settings className="w-4 h-4" /> {t('delivery_companies.card.connect_service')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Connection Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-visible animate-in fade-in zoom-in duration-200">
                        <div className="p-5 md:p-6 border-b border-gray-100 flex justify-between items-center">
                            <div>
                                <h3 className="text-lg md:text-xl font-black text-gray-900">{t('delivery_companies.modal.title', { name: selectedCompany?.name })}</h3>
                                <p className="text-xs md:text-sm text-gray-500 font-medium">{t('delivery_companies.modal.subtitle')}</p>
                            </div>
                            <button
                                onClick={closeModal}
                                className="p-2 hover:bg-gray-50 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>

                        <div className="p-5 md:p-6 space-y-4">
                            {Object.keys(credentials).map(field => (
                                <div key={field}>
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5">
                                        {field.replace(/_/g, ' ')
                                            .replace(/([A-Z])/g, ' $1')
                                            .replace(/^./, str => str.toUpperCase())}
                                    </label>
                                    <input
                                        type={field.toLowerCase().includes('password') ? "password" : "text"}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm font-bold"
                                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                                        value={credentials[field]}
                                        onChange={(e) => setCredentials(prev => ({ ...prev, [field]: e.target.value }))}
                                        autoComplete="off"
                                    />
                                </div>
                            ))}

                            {selectedCompany?.slug === 'sendit' && (
                                <div className="pt-2">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <MapPin size={12} className="text-blue-500" />
                                            Default Pickup City
                                        </label>
                                        <button 
                                            type="button"
                                            onClick={handleFetchCitiesForSendit}
                                            className="text-[10px] text-blue-600 font-bold px-2 py-1 bg-blue-50 rounded hover:bg-blue-100 flex items-center gap-1 transition-colors"
                                        >
                                            {loadingPickupCities ? <Loader2 size={10} className="animate-spin" /> : null}
                                            Charger les villes
                                        </button>
                                    </div>
                                    <CityCombobox
                                        cities={pickupCities}
                                        value={settings.pickupDistrictId}
                                        onChange={(id, name) => setSettings(prev => ({ ...prev, pickupDistrictId: id }))}
                                        placeholder="Select Pickup City"
                                        isLoading={loadingPickupCities}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1 font-medium">
                                        Sendit requires a pickup city for every shipment.
                                    </p>
                                </div>
                            )}

                            <div className="pt-4 flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={closeModal}
                                    className="flex-1 py-3 text-gray-500 font-black uppercase tracking-widest text-[11px] hover:bg-gray-50 rounded-xl transition-colors"
                                >
                                    {t('delivery_companies.modal.cancel')}
                                </button>
                                <button
                                    onClick={submitConnection}
                                    disabled={actionLoading}
                                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-[11px] rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100"
                                >
                                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                                    {t('delivery_companies.modal.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toasts */}
            <div className="fixed top-6 right-6 z-[110] flex flex-col gap-3">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl border animate-in slide-in-from-right-full duration-300 ${toast.type === 'success'
                            ? 'bg-white border-green-100 text-gray-900'
                            : 'bg-white border-red-100 text-gray-900'
                            }`}
                    >
                        {toast.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="font-semibold">{toast.message}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DeliveryCompanies;
